import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tabs, Input, Select, App, Tag, Space } from 'antd';
import { SearchOutlined, BlockOutlined, TransactionOutlined, CopyOutlined, UserOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import CopyableText from '../../components/CopyableText';
import { formatDateTime } from '../../utils/format';
import { 
  getExplorerStats, 
  getBlocks, 
  getTransactions, 
  getAddresses,
  getAssets,
  searchExplorer 
} from '../../services/explorerService';
import { getChains } from '../../services/chains';
import './style.css';

const { TabPane } = Tabs;
const { Search } = Input;

const Explorer = () => {
  const navigate = useNavigate();
  const { chainId } = useParams();
  const { message } = App.useApp();
  const [stats, setStats] = useState(null);
  const [chains, setChains] = useState([]);
  const [selectedChain, setSelectedChain] = useState(null);
  const [chainInfo, setChainInfo] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('blocks');
  const [blocksPagination, setBlocksPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [txPagination, setTxPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [addrPagination, setAddrPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  useEffect(() => {
    loadChains();
    loadStats();
  }, []);

  useEffect(() => {
    if (chainId && chains.length > 0) {
      const chainIdNum = parseInt(chainId);
      const chainExists = chains.some(c => c.id === chainIdNum);
      if (chainExists && selectedChain !== chainIdNum) {
        setSelectedChain(chainIdNum);
      }
    }
  }, [chainId, chains, selectedChain]);

  useEffect(() => {
    if (selectedChain && stats) {
      const chain = stats.chain_stats.find(c => c.chain_id === selectedChain);
      setChainInfo(chain);
    } else {
      setChainInfo(null);
    }
  }, [selectedChain, stats]);

  useEffect(() => {
    if (selectedChain) {
      if (activeTab === 'blocks') {
        loadBlocks(1);
      } else if (activeTab === 'transactions') {
        loadTransactions(1);
      } else if (activeTab === 'addresses') {
        loadAddresses(1);
      } else if (activeTab === 'assets') {
        loadAssets();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChain, activeTab]);

  const loadStats = async () => {
    try {
      const data = await getExplorerStats();
      setStats(data);
    } catch (error) {
      message.error('加载统计数据失败');
      console.error('Stats error:', error);
    }
  };

  const loadChains = async () => {
    try {
      const response = await getChains();
      setChains(response.data || []);
    } catch (error) {
      message.error('加载链列表失败');
      console.error('Chains error:', error);
    }
  };

  const loadBlocks = async (page = 1) => {
    if (!selectedChain) {
      return;
    }
    
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: blocksPagination.pageSize,
        chain_id: selectedChain 
      };
      
      const data = await getBlocks(params);
      setBlocks(data.blocks || []);
      setBlocksPagination(prev => ({
        ...prev,
        current: data.page,
        total: data.total
      }));
    } catch (error) {
      message.error('加载区块列表失败');
      console.error('Blocks error:', error);
    }
    setLoading(false);
  };

  const loadTransactions = async (page = 1) => {
    if (!selectedChain) {
      return;
    }
    
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: txPagination.pageSize,
        chain_id: selectedChain 
      };
      
      const data = await getTransactions(params);
      setTransactions(data.transactions || []);
      setTxPagination(prev => ({
        ...prev,
        current: data.page,
        total: data.total
      }));
    } catch (error) {
      message.error('加载交易列表失败');
      console.error('Transactions error:', error);
    }
    setLoading(false);
  };

  const loadAddresses = async (page = 1) => {
    if (!selectedChain) {
      return;
    }
    
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: addrPagination.pageSize,
        chain_id: selectedChain
      };
      const data = await getAddresses(params);
      setAddresses(data.addresses || []);
      setAddrPagination(prev => ({
        ...prev,
        current: data.page,
        total: data.total
      }));
    } catch (error) {
      message.error('加载地址列表失败');
      console.error('Addresses error:', error);
    }
    setLoading(false);
  };

  const loadAssets = async () => {
    if (!selectedChain) {
      return;
    }
    
    setLoading(true);
    try {
      const data = await getAssets({ chain_id: selectedChain });
      setAssets(data.assets || []);
    } catch (error) {
      message.error('加载资产列表失败');
      console.error('Assets error:', error);
    }
    setLoading(false);
  };

  const handleSearch = async (value) => {
    if (!value.trim()) {
      message.warning('请输入搜索内容');
      return;
    }
    
    try {
      const data = await searchExplorer(value);
      if (data.type === 'not_found') {
        message.warning('未找到相关信息');
      } else if (data.type === 'block') {
        message.success('找到区块');
        navigate(`/dashboard/explorer/block/${data.result.id}`);
      } else if (data.type === 'transaction') {
        message.success('找到交易');
        navigate(`/dashboard/explorer/transaction/${data.result.txid}`);
      } else if (data.type === 'address') {
        message.success('找到地址');
        navigate(`/dashboard/explorer/address/${data.result.id}`);
      } else if (data.type === 'asset') {
        message.success('找到资产');
        navigate(`/dashboard/explorer/asset/${data.result.id}`);
      }
    } catch (error) {
      message.error('搜索失败');
      console.error('Search error:', error);
    }
  };

  const handleChainChange = (chainId) => {
    setSelectedChain(chainId);
    navigate(`/dashboard/explorer/${chainId}`);
    setBlocks([]);
    setTransactions([]);
    setAddresses([]);
    setAssets([]);
    setBlocksPagination({ current: 1, pageSize: 10, total: 0 });
    setTxPagination({ current: 1, pageSize: 10, total: 0 });
    setAddrPagination({ current: 1, pageSize: 10, total: 0 });
  };

  const blockColumns = [
    {
      title: '区块高度',
      dataIndex: 'height',
      key: 'height',
      width: 120,
      render: (height, record) => (
        <a 
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/dashboard/explorer/block/${record.id}`);
          }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
        >
          {height}
        </a>
      )
    },
    {
      title: '区块哈希',
      dataIndex: 'hash',
      key: 'hash',
      width: 200,
      render: (text, record) => (
        <a 
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/dashboard/explorer/block/${record.id}`);
          }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
        >
          <CopyableText text={text} maxLength={16} />
        </a>
      )
    },
    {
      title: '交易数',
      dataIndex: 'transaction_count',
      key: 'transaction_count',
      width: 100
    },
    {
      title: '时间',
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      width: 180,
      render: (text) => formatDateTime(text)
    }
  ];

  const txColumns = [
    {
      title: '交易哈希',
      dataIndex: 'txid',
      key: 'txid',
      width: 200,
      render: (text) => (
        <a 
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/dashboard/explorer/transaction/${text}`);
          }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
        >
          <CopyableText text={text} maxLength={16} />
        </a>
      )
    },
    {
      title: '区块高度',
      dataIndex: 'block_height',
      key: 'block_height',
      width: 120
    },
    {
      title: '费用',
      dataIndex: 'fee',
      key: 'fee',
      width: 150,
      render: (fee) => fee ? parseFloat(fee).toFixed(8) : '-'
    },
    {
      title: '状态',
      dataIndex: 'is_mined',
      key: 'is_mined',
      width: 100,
      render: (mined) => (
        <Tag color={mined ? 'green' : 'orange'}>
          {mined ? '已确认' : '未确认'}
        </Tag>
      )
    },
    {
      title: '时间',
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      width: 180,
      render: (text) => formatDateTime(text)
    }
  ];

  const addressColumns = [
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: 250,
      render: (text, record) => (
        <a 
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/dashboard/explorer/address/${record.id}`);
          }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
        >
          <CopyableText text={text} maxLength={20} />
        </a>
      )
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 180,
      render: (balance) => balance ? parseFloat(balance).toFixed(8) : '0'
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100
    },
    {
      title: '状态',
      dataIndex: 'is_used',
      key: 'is_used',
      width: 100,
      render: (used) => (
        <Tag color={used ? 'blue' : 'default'}>
          {used ? '已使用' : '未使用'}
        </Tag>
      )
    }
  ];

  const assetColumns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text, record) => (
        <a 
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/dashboard/explorer/asset/${record.id}`);
          }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
        >
          {text}
        </a>
      )
    },
    {
      title: '符号',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 100
    },
    {
      title: '合约地址',
      dataIndex: 'contract_address',
      key: 'contract_address',
      width: 250,
      render: (text) => text ? <CopyableText text={text} maxLength={20} /> : '-'
    },
    {
      title: '精度',
      dataIndex: 'decimals',
      key: 'decimals',
      width: 80
    }
  ];

  return (
    <div className="explorer-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: '16px' }}>
        <Space size="large" style={{ flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>区块链浏览器</h2>
          <Select
            placeholder="选择区块链"
            style={{ width: 200, minWidth: 150 }}
            value={selectedChain}
            onChange={handleChainChange}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const label = option.children || '';
              return label.toString().toLowerCase().includes(input.toLowerCase());
            }}
          >
            {chains.map(chain => (
              <Select.Option key={chain.id} value={chain.id}>
                {chain.name}
              </Select.Option>
            ))}
          </Select>
        </Space>
        
        <Search
          placeholder="搜索区块哈希、交易哈希、地址或合约地址"
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          style={{ width: 400, maxWidth: '100%', minWidth: 200 }}
        />
      </div>

      {!selectedChain ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 16, color: '#999' }}>请选择一条区块链开始浏览</p>
            {stats && stats.chain_stats && stats.chain_stats.length > 0 && (
              <Row gutter={16} style={{ marginTop: 24 }}>
                {stats.chain_stats.map(chain => (
                  <Col xs={24} sm={12} md={8} key={chain.chain_id}>
                    <Card 
                      size="small" 
                      hoverable
                      onClick={() => handleChainChange(chain.chain_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <h3>{chain.chain_name}</h3>
                      <p>最新高度: {chain.latest_height}</p>
                      <p>区块数: {chain.total_blocks}</p>
                      <p>交易数: {chain.total_transactions}</p>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </Card>
      ) : (
        <>
          {chainInfo && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="当前链"
                    value={chainInfo.chain_name}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="最新高度"
                    value={chainInfo.latest_height}
                    prefix={<BlockOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="总区块数"
                    value={chainInfo.total_blocks}
                    prefix={<BlockOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="总交易数"
                    value={chainInfo.total_transactions}
                    prefix={<TransactionOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {chainInfo && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="地址数"
                    value={chainInfo.total_addresses || 0}
                    prefix={<UserOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="资产数"
                    value={chainInfo.total_assets || 0}
                    prefix={<AppstoreOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <Card>
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab="区块" key="blocks">
                <Table
                  columns={blockColumns}
                  dataSource={blocks}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 800 }}
                  pagination={{
                    ...blocksPagination,
                    onChange: loadBlocks
                  }}
                />
              </TabPane>

              <TabPane tab="交易" key="transactions">
                <Table
                  columns={txColumns}
                  dataSource={transactions}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 800 }}
                  pagination={{
                    ...txPagination,
                    onChange: loadTransactions
                  }}
                />
              </TabPane>

              <TabPane tab="地址" key="addresses">
                <Table
                  columns={addressColumns}
                  dataSource={addresses}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 800 }}
                  pagination={{
                    ...addrPagination,
                    onChange: loadAddresses
                  }}
                />
              </TabPane>

              <TabPane tab="资产" key="assets">
                <Table
                  columns={assetColumns}
                  dataSource={assets}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 800 }}
                  pagination={false}
                />
              </TabPane>
            </Tabs>
          </Card>
        </>
      )}
    </div>
  );
};

export default Explorer;
